import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { adminStatsService, realtimeService } from '../../../services/supabaseService';

const RealTimeMonitor = () => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('rides');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const orders = await adminStatsService?.getRecentOrders(20);
        setActiveOrders(orders);
      } catch (err) {
        console.error('Failed to load orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();

    // Subscribe to real-time order updates
    const channel = realtimeService?.subscribeToOrderStatusChanges((payload) => {
      setActiveOrders(prev => {
        const updated = prev?.map(o =>
          o?.id === payload?.new?.id ? { ...o, status: payload?.new?.status } : o
        );
        return updated;
      });
    });

    return () => realtimeService?.unsubscribe(channel);
  }, []);

  const rides = activeOrders?.filter(o => o?.serviceType === 'ride' && !['delivered', 'cancelled']?.includes(o?.status));
  const deliveries = activeOrders?.filter(o => o?.serviceType === 'delivery' && !['delivered', 'cancelled']?.includes(o?.status));

  const getStatusColor = (status) => {
    switch (status) {
      case 'en_route': case 'picked_up': return 'success';
      case 'driver_assigned': return 'warning';
      case 'confirmed': return 'secondary';
      case 'pending': return 'warning';
      default: return 'text-muted';
    }
  };

  const handleIntervention = (id, type) => {
    console.log(`Intervention requested for ${type} ${id}`);
  };

  const renderRidesView = () => (
    <div className="space-y-3">
      {loading ? (
        <div className="text-center py-6 text-text-muted text-sm">Loading rides...</div>
      ) : rides?.length === 0 ? (
        <div className="text-center py-6 text-text-muted text-sm">No active rides</div>
      ) : rides?.map((ride) => (
        <div key={ride?.id} className="p-3 border border-border rounded-lg hover:bg-surface-hover transition-colors duration-200">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full bg-${getStatusColor(ride?.status)}`}></div>
              <span className="font-medium text-text-primary">{ride?.orderNumber}</span>
              <span className={`text-xs px-2 py-1 rounded-full bg-${getStatusColor(ride?.status)}-100 text-${getStatusColor(ride?.status)}`}>
                {ride?.status?.replace('_', ' ')}
              </span>
            </div>
            <button onClick={() => handleIntervention(ride?.id, 'ride')} className="p-1 hover:bg-surface-hover rounded transition-colors duration-200">
              <Icon name="MoreHorizontal" size={14} />
            </button>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Customer:</span>
              <span className="text-text-primary">{ride?.customer?.fullName || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Driver:</span>
              <span className="text-text-primary">{ride?.driver?.name || 'Unassigned'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Route:</span>
              <span className="text-text-primary text-right text-xs">{ride?.pickupAddress} → {ride?.dropoffAddress}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Fare:</span>
              <span className="font-medium text-text-primary">KES {ride?.totalAmount}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderDeliveriesView = () => (
    <div className="space-y-3">
      {loading ? (
        <div className="text-center py-6 text-text-muted text-sm">Loading deliveries...</div>
      ) : deliveries?.length === 0 ? (
        <div className="text-center py-6 text-text-muted text-sm">No active deliveries</div>
      ) : deliveries?.map((delivery) => (
        <div key={delivery?.id} className="p-3 border border-border rounded-lg hover:bg-surface-hover transition-colors duration-200">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full bg-${getStatusColor(delivery?.status)}`}></div>
              <span className="font-medium text-text-primary">{delivery?.orderNumber}</span>
              <span className={`text-xs px-2 py-1 rounded-full bg-${getStatusColor(delivery?.status)}-100 text-${getStatusColor(delivery?.status)}`}>
                {delivery?.status?.replace('_', ' ')}
              </span>
            </div>
            <button onClick={() => handleIntervention(delivery?.id, 'delivery')} className="p-1 hover:bg-surface-hover rounded transition-colors duration-200">
              <Icon name="MoreHorizontal" size={14} />
            </button>
          </div>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Customer:</span>
              <span className="text-text-primary">{delivery?.customer?.fullName || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Driver:</span>
              <span className="text-text-primary">{delivery?.driver?.name || 'Unassigned'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Route:</span>
              <span className="text-text-primary text-right text-xs">{delivery?.pickupAddress} → {delivery?.dropoffAddress}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-muted">Fee:</span>
              <span className="font-medium text-text-primary">KES {delivery?.totalAmount}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Real-Time Monitor</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
          <span className="text-xs text-text-muted">Live</span>
        </div>
      </div>
      <div className="flex space-x-1 mb-4 bg-surface rounded-lg p-1">
        {[
          { id: 'rides', label: 'Rides', count: rides?.length },
          { id: 'deliveries', label: 'Deliveries', count: deliveries?.length },
        ]?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setSelectedView(tab?.id)}
            className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors duration-200 ${
              selectedView === tab?.id
                ? 'bg-white shadow text-text-primary'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            {tab?.label} ({tab?.count})
          </button>
        ))}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {selectedView === 'rides' ? renderRidesView() : renderDeliveriesView()}
      </div>
    </div>
  );
};

export default RealTimeMonitor;